import type { SiteChatPlacement } from '@/constants/siteChat';

/**
 * Client-side bridge to the bots server's launcher API
 * (`window.easyPeasyChat`, see web-app `app/script/src/api.ts`).
 */
export interface SiteChatContact {
  email?: string;
  name?: string;
  externalId?: string;
}

export interface EasyPeasyChatApi {
  hidden?: boolean;
  /** Pre-load contact hint; the launcher reads it when it mounts. */
  contact?: SiteChatContact | null;
  setContact(contact: SiteChatContact | null): void;
  open(): void;
  close(): void;
  toggle(): void;
  show(): void;
  hide(): void;
  destroy(): void;
  init(): void;
  isOpen(): boolean;
  isVisible(): boolean;
  isMounted(): boolean;
}

declare global {
  interface Window {
    easyPeasyChat?: Partial<EasyPeasyChatApi>;
  }
}

/** Fired by the launcher once its DOM exists. */
export const SITE_CHAT_READY_EVENT = 'easy-peasy-chat:ready';

const hasLauncherApi = (
  api: Partial<EasyPeasyChatApi> | undefined,
): api is EasyPeasyChatApi => typeof api?.show === 'function';

/**
 * Sets the pre-load flag the launcher reads when it mounts, so a page where
 * the bubble must be hidden never flashes it before the first `hide()`.
 */
export function preconfigureSiteChat(placement: SiteChatPlacement): void {
  if (typeof window === 'undefined') return;
  const api = (window.easyPeasyChat ||= {});
  api.hidden = placement !== 'show';
}

/**
 * Applies a placement to the launcher. Falls back to toggling the launcher's
 * DOM directly while the bots server still serves a `chat.min.js` without the
 * JavaScript API (kopi deployed before web-app).
 */
export function applySiteChatPlacement(placement: SiteChatPlacement): void {
  if (typeof window === 'undefined') return;
  preconfigureSiteChat(placement);
  const api = window.easyPeasyChat;
  if (hasLauncherApi(api)) {
    if (placement === 'show') {
      api.init();
      api.show();
    } else if (placement === 'hide') {
      api.hide();
    } else {
      api.destroy();
    }
    return;
  }
  legacyApplyPlacement(placement);
}

function legacyApplyPlacement(placement: SiteChatPlacement): void {
  const panel = document.getElementById('dialoq');
  const launcher = document.getElementById('dialoq-btn')?.parentElement;
  const bubbles = document.getElementById('dialoq-message-bubbles');
  if (placement === 'show') {
    if (launcher) launcher.style.display = 'block';
    return;
  }
  if (panel) panel.style.display = 'none';
  if (launcher) launcher.style.display = 'none';
  if (bubbles) bubbles.style.display = 'none';
  if (placement === 'unmount') {
    panel?.remove();
    launcher?.remove();
  }
}

/** Opens the support agent, e.g. from a "Chat with us" button. */
export function openSiteChat(): boolean {
  const api = typeof window === 'undefined' ? undefined : window.easyPeasyChat;
  if (!hasLauncherApi(api)) return false;
  api.show();
  api.open();
  return true;
}

/**
 * Tags the visitor's conversations with the signed-in account (email/name)
 * so the support agent's Conversations view shows who was chatting. This is
 * an unverified attribution hint — the same trust level as a lead form — and
 * the bots server stores it as such.
 */
export function setSiteChatContact(contact: SiteChatContact | null): void {
  if (typeof window === 'undefined') return;
  const api = (window.easyPeasyChat ||= {});
  if (typeof api.setContact === 'function') {
    api.setContact(contact);
  } else {
    // Launcher not loaded yet: the pre-load field is picked up on mount.
    api.contact = contact;
  }
}
